import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — AdminOne Startups + Grants Intelligence (Phase 26).
 *
 * Strictly additive. AdminOne-only command surface. Public read-only data feeds
 * also expose /api/oracle/startups and /api/oracle/grants for member-tier UIs.
 */

const TABS = [
  { id: "summary",  label: "SUMMARY",       icon: "★" },
  { id: "startups", label: "STARTUPS",      icon: "🚀" },
  { id: "grants",   label: "GRANTS",        icon: "💼" },
];

const DIR_COLOR = { bullish: "#10b981", bearish: "#ef4444", neutral: "#c5a059" };

function ScoreBar({ value, palette = "opportunity" }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const grads = {
    opportunity: "linear-gradient(90deg, #ffd966, #c5a059)",
    confidence:  "linear-gradient(90deg, #34d399, #10b981)",
    risk:        "linear-gradient(90deg, #fda4af, #ef4444)",
  };
  return (
    <div style={S.bar}>
      <div style={{ ...S.barFill, width: `${v}%`, background: grads[palette] || grads.opportunity }} />
    </div>
  );
}

function DirPill({ d }) {
  const dir = (d || "neutral").toLowerCase();
  const c = DIR_COLOR[dir] || DIR_COLOR.neutral;
  return (
    <span style={{ ...S.dirPill, color: c, borderColor: c, background: `${c}1a` }}>
      {dir.toUpperCase()}
    </span>
  );
}

function fmtUSD(amount) {
  if (amount == null) return "—";
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(amount % 1_000_000 ? 2 : 1)}M`;
  if (amount >= 1_000)     return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount}`;
}

function StartupCard({ item, testid }) {
  return (
    <div style={S.card} data-testid={testid}>
      <div style={S.cardHead}>
        <strong style={S.cardTitle}>{item.name}</strong>
        <DirPill d={item.direction} />
      </div>
      <div style={S.metaRow}><span style={S.metaLabel}>SECTOR</span><code style={S.metaVal}>{item.sector}</code></div>
      <div style={S.metaRow}><span style={S.metaLabel}>REGION</span><code style={S.metaVal}>{item.region}</code></div>
      <div style={S.metaRow}><span style={S.metaLabel}>STAGE</span><code style={S.metaVal}>{item.stage}</code></div>
      <div style={S.metaRow}><span style={S.metaLabel}>RAISE</span><code style={{ ...S.metaVal, color: "#c5a059" }}>${item.raise_usd_m}M</code></div>
      <div style={S.metaRow}><span style={S.metaLabel}>LEAD</span><code style={S.metaVal}>{item.lead_investor}</code></div>
      <div style={S.scoreRow}>
        <div style={S.scoreCell}>
          <span style={S.scoreLabel}>OPP</span>
          <span style={{ ...S.scoreVal, color: "#c5a059" }}>{item.opportunity_score}%</span>
          <ScoreBar value={item.opportunity_score} palette="opportunity" />
        </div>
        <div style={S.scoreCell}>
          <span style={S.scoreLabel}>CONF</span>
          <span style={{ ...S.scoreVal, color: "#10b981" }}>{item.confidence_score}%</span>
          <ScoreBar value={item.confidence_score} palette="confidence" />
        </div>
        <div style={S.scoreCell}>
          <span style={S.scoreLabel}>RISK</span>
          <span style={{ ...S.scoreVal, color: "#fca5a5" }}>{item.risk_score}%</span>
          <ScoreBar value={item.risk_score} palette="risk" />
        </div>
      </div>
      <p style={S.uncertainty}>{item.uncertainty_note}</p>
      <ComplianceLock variant="inline" short testid={`${testid}-compliance`} />
    </div>
  );
}

function GrantCard({ item, testid }) {
  const urgency = item.deadline_days <= 21
    ? { color: "#ef4444", label: "DUE SOON" }
    : item.deadline_days <= 42
      ? { color: "#c5a059", label: "OPEN" }
      : { color: "#10b981", label: "PLENTY OF TIME" };
  return (
    <div style={S.card} data-testid={testid}>
      <div style={S.cardHead}>
        <strong style={S.cardTitle}>{item.name}</strong>
        <span style={{ ...S.dirPill, color: urgency.color, borderColor: urgency.color, background: `${urgency.color}1a` }}>
          {urgency.label}
        </span>
      </div>
      <div style={S.metaRow}><span style={S.metaLabel}>AGENCY</span><code style={S.metaVal}>{item.agency}</code></div>
      <div style={S.metaRow}><span style={S.metaLabel}>CATEGORY</span><code style={S.metaVal}>{item.category}</code></div>
      <div style={S.metaRow}><span style={S.metaLabel}>AWARD</span><code style={{ ...S.metaVal, color: "#c5a059" }}>{fmtUSD(item.award_usd)}</code></div>
      <div style={S.metaRow}><span style={S.metaLabel}>DEADLINE</span><code style={S.metaVal}>{item.deadline} · {item.deadline_days}d</code></div>
      <p style={S.bodySmall}><b style={{ color: "#c5a059" }}>Eligibility:</b> {item.eligibility}</p>
      <details style={S.details}>
        <summary style={S.detailsSummary}>Application steps</summary>
        <ol style={S.stepsList}>
          {(item.application_steps || []).map((step, i) => <li key={`s-${i}`}>{step}</li>)}
        </ol>
      </details>
      <p style={S.uncertainty}>{item.uncertainty_note}</p>
      <ComplianceLock variant="inline" short testid={`${testid}-compliance`} />
    </div>
  );
}

export default function AdminStartupsGrants() {
  const [tab, setTab] = useState("summary");
  const [startups, setStartups] = useState([]);
  const [grants, setGrants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [s, g, sm] = await Promise.all([
        adminApi.startupsPublic(),
        adminApi.grantsPublic(),
        adminApi.startupsGrantsSummary(),
      ]);
      setStartups(s.data?.items || []);
      setGrants(g.data?.items || []);
      setSummary(sm.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    summary:  (summary?.startups_top?.length || 0) + (summary?.grants_top?.length || 0),
    startups: startups.length,
    grants:   grants.length,
  }), [summary, startups, grants]);

  return (
    <div style={S.canvas} data-testid="startups-grants-page">
      {/* Phase 26C — Mandated Compliance Lock™ banner */}
      <ComplianceLock variant="banner" testid="sg-compliance-top" />
      <div style={S.banner}>
        <span style={{ color: "#10b981" }}>●</span>
        <span>
          <strong>Startups + Grants Intelligence</strong> · deterministic public-data demo ·
          research only · not financial advice · verify on official portals
        </span>
      </div>

      <header style={S.header}>
        <div>
          <h1 style={S.title}>Startups &amp; Grants Watchtower™</h1>
          <p style={S.subtitle}>
            VC activity, funding rounds, federal + state grants, accelerator + minority business
            programs. NiSi-scored. Sorted by urgency.
          </p>
        </div>
        <div style={S.headerActions}>
          <span style={S.livePill} data-testid="sg-live-pill">LIVE·DEMO</span>
          <button type="button" onClick={load} disabled={busy} style={S.refreshBtn} data-testid="sg-refresh">
            {busy ? "…" : "↻ Refresh"}
          </button>
        </div>
      </header>

      {err && <div style={S.errBanner} data-testid="sg-error">⚠ {err}</div>}

      <nav style={S.tabs} role="tablist" aria-label="Startups + Grants tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            data-testid={`sg-tab-${t.id}`}
            style={{
              ...S.tab,
              color: tab === t.id ? "#c5a059" : "#94a3b8",
              borderBottomColor: tab === t.id ? "#c5a059" : "transparent",
            }}
          >
            <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
            {counts[t.id] ? <span style={S.tabCount}>{counts[t.id]}</span> : null}
          </button>
        ))}
      </nav>

      <main style={S.main}>
        {!startups.length && !grants.length && !err && <p style={S.empty}>Loading…</p>}

        {tab === "summary" && summary && (
          <section style={S.panel} data-testid="sg-section-summary">
            <h2 style={S.heading}>
              Daily Briefing Highlights
              <span style={S.countChip}>{summary.startups_total} startups · {summary.grants_total} grants</span>
            </h2>
            <h3 style={{ color: "#c5a059", margin: "14px 0 8px" }}>Top Startup Signals</h3>
            <div style={S.grid}>
              {summary.startups_top.map((it, i) => (
                <StartupCard key={`s-top-${i}`} item={it} testid={`sg-summary-startup-${i}`} />
              ))}
            </div>
            <h3 style={{ color: "#c5a059", margin: "20px 0 8px" }}>Most Urgent Grants</h3>
            <div style={S.grid}>
              {summary.grants_top.map((it, i) => (
                <GrantCard key={`g-top-${i}`} item={it} testid={`sg-summary-grant-${i}`} />
              ))}
            </div>
          </section>
        )}

        {tab === "startups" && (
          <section style={S.panel} data-testid="sg-section-startups">
            <h2 style={S.heading}>Startups Pipeline<span style={S.countChip}>{startups.length} entries</span></h2>
            <div style={S.grid}>
              {startups.map((it, i) => (
                <StartupCard key={`startup-${i}`} item={it} testid={`sg-startup-${i}`} />
              ))}
            </div>
          </section>
        )}

        {tab === "grants" && (
          <section style={S.panel} data-testid="sg-section-grants">
            <h2 style={S.heading}>Grants Pipeline<span style={S.countChip}>{grants.length} entries</span></h2>
            <div style={S.grid}>
              {grants.map((it, i) => (
                <GrantCard key={`grant-${i}`} item={it} testid={`sg-grant-${i}`} />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

const S = {
  canvas: { minHeight: "100vh", background: "radial-gradient(circle at top left, rgba(16,185,129,0.10), #040608 30%, #020304 100%)", color: "#f8fafc", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  banner: { display: "flex", alignItems: "center", gap: 10, padding: "9px 22px", background: "linear-gradient(90deg, rgba(16,185,129,0.10), rgba(197,160,89,0.06))", borderBottom: "1px solid rgba(16,185,129,0.18)", fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.04em", color: "#d1fae5" },
  header: { padding: "20px 26px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  title: { color: "#c5a059", fontSize: 24, margin: 0, fontWeight: 800 },
  subtitle: { color: "#94a3b8", fontSize: 13, maxWidth: 820, marginTop: 6 },
  headerActions: { display: "flex", gap: 8, alignItems: "center" },
  livePill: { color: "#10b981", border: "1px solid rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.10)", padding: "6px 11px", borderRadius: 999, fontSize: 10.5, fontWeight: 900, fontFamily: "monospace", letterSpacing: "0.10em" },
  refreshBtn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em" },
  errBanner: { margin: "10px 26px 0", padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12.5 },
  tabs: { display: "flex", gap: 18, padding: "0 26px", background: "rgba(11,15,20,0.92)", borderBottom: "1px solid rgba(255,255,255,0.05)", overflowX: "auto" },
  tab: { background: "transparent", border: "none", borderBottom: "2px solid", padding: "14px 0", fontSize: 11.5, fontWeight: 900, cursor: "pointer", letterSpacing: "0.08em", fontFamily: "'Space Mono', monospace", whiteSpace: "nowrap", display: "flex", alignItems: "center" },
  tabCount: { marginLeft: 8, background: "rgba(197,160,89,0.18)", color: "#ffd966", padding: "2px 8px", borderRadius: 999, fontSize: 10 },
  main: { padding: "20px 26px 30px" },
  panel: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.14)", borderRadius: 16, padding: 22 },
  heading: { color: "#e2e8f0", marginTop: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 16, flexWrap: "wrap" },
  countChip: { fontSize: 11, fontFamily: "'Space Mono', monospace", letterSpacing: "0.08em", background: "rgba(197,160,89,0.10)", color: "#c5a059", border: "1px solid rgba(197,160,89,0.28)", padding: "3px 9px", borderRadius: 999 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 12, marginTop: 12 },
  card: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 6 },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  cardTitle: { color: "#e2e8f0", fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dirPill: { fontSize: 10, fontFamily: "'Space Mono', monospace", letterSpacing: "0.10em", fontWeight: 800, padding: "3px 8px", border: "1px solid", borderRadius: 999 },
  scoreRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 6 },
  scoreCell: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: "6px 8px", display: "flex", flexDirection: "column", gap: 3 },
  scoreLabel: { fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.10em", color: "#64748b" },
  scoreVal: { fontSize: 14, fontWeight: 800, fontFamily: "'Figtree', sans-serif" },
  bar: { height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 4, transition: "width 280ms ease" },
  metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 4 },
  metaLabel: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em" },
  metaVal: { background: "rgba(255,255,255,0.04)", color: "#cbd5e1", padding: "2px 6px", borderRadius: 6, fontFamily: "'Space Mono', monospace", fontSize: 11 },
  bodySmall: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.4, margin: "6px 0 0" },
  uncertainty: { color: "#94a3b8", fontSize: 11, fontStyle: "italic", margin: "8px 0 0" },
  details: { marginTop: 6 },
  detailsSummary: { cursor: "pointer", color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.10em" },
  stepsList: { margin: "8px 0 0 18px", color: "#cbd5e1", fontSize: 12, lineHeight: 1.5 },
  empty: { gridColumn: "1 / -1", color: "#94a3b8", textAlign: "center", padding: 28, border: "1px dashed rgba(255,255,255,0.10)", borderRadius: 12, fontFamily: "'Space Mono', monospace", fontSize: 12.5 },
};
